/**
 * Notice Individual preset (L_TASK_EFILING_DATA / doc formData) → ControllerAppointmentForm initial-data
 */

import {
  inferSgIdTypeCd,
  parseManualAddressText,
  splitMobileNumber,
  type ParsedManualAddress,
} from '@/features/startTask/utils/manualOfficerContactData';
import { mergeOfficerContactDataForPreset } from '../../../appoint-director/composables/adaptOfficerAppointmentEfilingFormData';
import {
  getB04Options,
  nationalityLabelForOutput,
  resolveCmBaseCodeValue,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export const PRESET_MANUAL_CONTROLLER_CONTACT_CD = '__PRESET_EFILING_CONTACT__';

/** presetForm.formModel + 저장 contactData + flat task formData 병합 */
export function flattenNoticeControllerPresetSource(
  src: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!src || typeof src !== 'object') return {};
  const preset = src.presetForm;
  let formModel: Record<string, unknown> = {};
  if (preset && typeof preset === 'object') {
    const pf = preset as Record<string, unknown>;
    if (pf.formModel && typeof pf.formModel === 'object') {
      formModel = pf.formModel as Record<string, unknown>;
    } else {
      formModel = pf;
    }
  }
  const savedContact =
    src.contactData && typeof src.contactData === 'object'
      ? (src.contactData as Record<string, unknown>)
      : {};
  return { ...savedContact, ...formModel, ...src };
}

function buildPresetControllerContactOption(
  displayName: string,
  contactCd: string,
): { title: string; value: string } | undefined {
  const name = trim(displayName);
  const cd = trim(contactCd);
  if (!name) return undefined;
  if (cd) return { title: name, value: cd };
  return { title: name, value: PRESET_MANUAL_CONTROLLER_CONTACT_CD };
}

/** DD/MM/YYYY or ISO → YYYY-MM-DD for date pickers */
function normalizeDateForEfiling(value: unknown): string {
  const raw = trim(value);
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.substring(0, 10);
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return raw;
}

export function isNoticeIndividualFlatFormData(fd: Record<string, unknown> | null | undefined): boolean {
  if (!fd || typeof fd !== 'object') return false;
  if (fd.formFields && typeof fd.formFields === 'object') return false;
  if (fd.saveController) return false;
  if (trim(fd.controller_corporate_name) || trim(fd.controllerCorporateName)) return false;
  if (trim(fd.categoryCd) === 'B09001' || trim(fd.categoryCode) === 'B09001') return false;
  return Boolean(
    fd.notice_date
    || fd.controller_full_name
    || fd.controllerFullName
    || fd.notice_recipient_name
    || fd.company_name,
  );
}

export function parseEfilingFormDataColumn(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

export function buildPrimaryAddressFields(parsed: ParsedManualAddress): Record<string, string> {
  if (parsed.typeCd === 'B07001') {
    return {
      POSTAL_CODE: parsed.postalCode,
      postalCode: parsed.postalCode,
      BLOCK_NO: parsed.blockNo,
      blockNo: parsed.blockNo,
      STREET_NAME: parsed.streetName,
      streetName: parsed.streetName,
      BUILDING_NAME: parsed.buildingName,
      buildingName: parsed.buildingName,
      ADDR_LEVEL: parsed.addrLevel,
      addrLevel: parsed.addrLevel,
      ADDR_UNIT: parsed.addrUnit,
      addrUnit: parsed.addrUnit,
      FOREIGN_ADDRESS_LINE_1: '',
      foreignAddressLine1: '',
      FOREIGN_ADDRESS_LINE_2: '',
      foreignAddressLine2: '',
    };
  }
  return {
    POSTAL_CODE: '',
    postalCode: '',
    BLOCK_NO: '',
    blockNo: '',
    STREET_NAME: '',
    streetName: '',
    BUILDING_NAME: '',
    buildingName: '',
    ADDR_LEVEL: '',
    addrLevel: '',
    ADDR_UNIT: '',
    addrUnit: '',
    FOREIGN_ADDRESS_LINE_1: parsed.foreignAddressLine1,
    foreignAddressLine1: parsed.foreignAddressLine1,
    FOREIGN_ADDRESS_LINE_2: parsed.foreignAddressLine2,
    foreignAddressLine2: parsed.foreignAddressLine2,
  };
}

export function buildAlternateAddressFields(parsed: ParsedManualAddress): Record<string, string> {
  if (parsed.typeCd === 'B07001') {
    return {
      ALTERNATE_ADDRESS_CD: 'B07001',
      alternateAddressCd: 'B07001',
      ALT_POSTAL_CODE: parsed.postalCode,
      altPostalCode: parsed.postalCode,
      ALT_BLOCK_NO: parsed.blockNo,
      altBlockNo: parsed.blockNo,
      ALT_STREET_NAME: parsed.streetName,
      altStreetName: parsed.streetName,
      ALT_BUILDING_NAME: parsed.buildingName,
      altBuildingName: parsed.buildingName,
      ALT_ADDR_LEVEL: parsed.addrLevel,
      altAddrLevel: parsed.addrLevel,
      ALT_ADDR_UNIT: parsed.addrUnit,
      altAddrUnit: parsed.addrUnit,
      ALT_FOREIGN_ADDRESS_LINE_1: '',
      altForeignAddressLine1: '',
      ALT_FOREIGN_ADDRESS_LINE_2: '',
      altForeignAddressLine2: '',
    };
  }
  return {
    ALTERNATE_ADDRESS_CD: 'B07002',
    alternateAddressCd: 'B07002',
    ALT_POSTAL_CODE: '',
    altPostalCode: '',
    ALT_BLOCK_NO: '',
    altBlockNo: '',
    ALT_STREET_NAME: '',
    altStreetName: '',
    ALT_BUILDING_NAME: '',
    altBuildingName: '',
    ALT_ADDR_LEVEL: '',
    altAddrLevel: '',
    ALT_ADDR_UNIT: '',
    altAddrUnit: '',
    ALT_FOREIGN_ADDRESS_LINE_1: parsed.foreignAddressLine1,
    altForeignAddressLine1: parsed.foreignAddressLine1,
    ALT_FOREIGN_ADDRESS_LINE_2: parsed.foreignAddressLine2,
    altForeignAddressLine2: parsed.foreignAddressLine2,
  };
}

export function buildNoticeIndividualContactDataFromPreset(
  src: Record<string, unknown>,
): Record<string, unknown> {
  const safe = flattenNoticeControllerPresetSource(src);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = safe[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const fullName = pick('controller_full_name', 'controllerFullName');
  const addrText = pick('controller_residential_address', 'controllerResidentialAddress');
  const parsedAddr = parseManualAddressText(addrText);
  const altAddrText = pick('controller_alternate_address', 'controllerAlternateAddress');
  const parsedAltAddr = altAddrText ? parseManualAddressText(altAddrText) : null;
  const became = normalizeDateForEfiling(pick('controller_became_date', 'controllerBecameDate'));
  const contactCd = pick('contactCd', 'contact_cd', 'controllerCd');
  const email = pick('controller_email', 'controllerEmail');
  let mobileNo = pick('controller_contact_no', 'controllerContactNo', 'MOBILE_NO', 'mobileNo');
  let countryCd = pick('controller_mobile_country_cd', 'controllerMobileCountryCd', 'COUNTRY_CD', 'countryCd');
  let areaCode = pick('controller_mobile_area_code', 'controllerMobileAreaCode', 'AREA_CODE', 'areaCode');
  if (!countryCd && mobileNo) {
    const phone = splitMobileNumber(mobileNo);
    if (phone.countryCd) countryCd = phone.countryCd;
    if (phone.areaCode && !areaCode) areaCode = phone.areaCode;
    if (phone.mobileNo) mobileNo = phone.mobileNo;
  }
  const nationalityRaw = pick('controller_nationality', 'controllerNationality');
  const nationalityCd = resolveCmBaseCodeValue(nationalityRaw, getB04Options());
  const nationalityText = nationalityLabelForOutput(nationalityCd) || nationalityRaw;
  const dob = normalizeDateForEfiling(pick('controller_date_of_birth', 'controllerDateOfBirth'));
  const idNo = pick('controller_identification_no', 'controllerIdentificationNo');
  const idTypeCd =
    pick('controller_id_type_cd', 'controllerIdTypeCd', 'ID_TYPE_CD', 'idTypeCd') || inferSgIdTypeCd(idNo);
  const aliases = pick('controller_aliases', 'controllerAliases');

  const addressFields = buildPrimaryAddressFields(parsedAddr);
  const alternateAddressFields = parsedAltAddr ? buildAlternateAddressFields(parsedAltAddr) : {};

  return {
    contactCd,
    CONTACT_CD: contactCd,
    categoryCd: 'B09002',
    CATEGORY_CD: 'B09002',
    contactName: fullName,
    CONTACT_NAME: fullName,
    CONTACT_REG_NO: idNo,
    contactRegNo: idNo,
    ID_TYPE_CD: idTypeCd,
    idTypeCd,
    NATIONALITY_CD: nationalityCd,
    nationalityCd: nationalityCd,
    nationalityText: nationalityText,
    DATE_OF_BIRTH: dob,
    dateOfBirth: dob,
    ALIASES: aliases,
    aliases,
    CONTACT_EMAIL: email,
    contactEmail: email,
    MOBILE_NO: mobileNo,
    mobileNo,
    COUNTRY_CD: countryCd,
    countryCd,
    AREA_CODE: areaCode,
    areaCode,
    ADDRESS_TYPE_CD: parsedAddr.typeCd,
    addressTypeCd: parsedAddr.typeCd,
    ...addressFields,
    ...alternateAddressFields,
    dateOfBecoming: became,
    DATE_OF_BECOMING: became,
    confirmationByController: 'No',
    CONFIRMATION_BY_CONTROLLER: 'No',
  };
}

export function adaptNoticeIndividualToControllerAppointmentFormData(
  src: Record<string, unknown> | null | undefined,
  entityCdFallback = '',
  entityNameFallback = '',
): Record<string, unknown> {
  const safe = flattenNoticeControllerPresetSource(src);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = (safe as Record<string, unknown>)[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const entity = pick('entity', 'entityCd', 'icpCd') || entityCdFallback;
  const entityUen = pick(
    'entity_uen',
    'entityUEN',
    'EntityUEN',
    'company_registration_no',
    'companyRegistrationNo',
    'entityRegNo',
  );
  const contactCd = pick('contactCd', 'contact_cd');
  const fullName = pick('controller_full_name', 'controllerFullName');
  const became = normalizeDateForEfiling(pick('controller_became_date', 'controllerBecameDate'));
  const existingContact =
    safe.contactData && typeof safe.contactData === 'object'
      ? (safe.contactData as Record<string, unknown>)
      : undefined;
  const builtContact = buildNoticeIndividualContactDataFromPreset(safe);
  const contactData = mergeOfficerContactDataForPreset(builtContact, existingContact, contactCd);
  const presetControllerContactOption = buildPresetControllerContactOption(fullName, contactCd);

  return {
    ...safe,
    entity,
    entityCd: entity,
    entityUEN: entityUen,
    entity_uen: entityUen,
    company_registration_no: entityUen,
    entityRegNo: entityUen,
    categoryCd: 'B09002',
    categoryCode: 'B09002',
    controller_full_name: fullName,
    controllerFullName: fullName,
    contactCd,
    contact_cd: contactCd,
    controllerCd: contactCd,
    controller_became_date: became,
    dateOfBecoming: became,
    dateOfAppointed: { value: became },
    contactData,
    presetControllerContactOption,
    entityName: { value: pick('company_name', 'companyName', 'entityName') || entityNameFallback },
  };
}

/** Foreign residential string → Line 1 / Line 2 (ACRA e-filing layout) */
export function splitForeignAddress(full: string): { line1: string; line2: string } {
  const s = full.trim();
  if (!s) return { line1: '', line2: '' };
  const doubleComma = s.indexOf(',,');
  if (doubleComma >= 0) {
    const line1 = s.slice(0, doubleComma).trim();
    const line2 = s.slice(doubleComma + 2).trim();
    return {
      line1: line1.endsWith(',') ? line1 : `${line1},`,
      line2,
    };
  }
  const republicIdx = s.search(/\bREPUBLIC OF\b/i);
  if (republicIdx > 0) {
    const before = s.slice(0, republicIdx).replace(/,\s*$/, '').trim();
    const cityPart = s.slice(republicIdx).trim();
    const lastComma = before.lastIndexOf(',');
    if (lastComma > 0) {
      return {
        line1: `${before.slice(0, lastComma + 1).trim()}${before.slice(0, lastComma + 1).endsWith(',') ? '' : ''}`,
        line2: `${before.slice(lastComma + 1).trim()} ${cityPart}`.trim(),
      };
    }
    return { line1: before.endsWith(',') ? before : `${before},`, line2: cityPart };
  }
  return { line1: s, line2: '' };
}

type CodeOption = { title?: string; value?: string; bigo1?: string; bigo3?: string };

/** Match "Korean, South" etc. to CM B05 country TOTAL_CODE (e.g. South Korea +82) */
export function resolveCountryCdFromNationalityText(
  nationalityText: string,
  countryOptions: CodeOption[],
  nationalityOptions: CodeOption[] = [],
): string {
  const norm = nationalityText.trim().toLowerCase();
  if (!norm) return '';

  for (const opt of countryOptions) {
    const title = String(opt.title || '').toLowerCase();
    if ((norm.includes('korea') || norm.includes('korean')) && title.includes('korea')) {
      return String(opt.value || '');
    }
  }

  for (const nat of nationalityOptions) {
    const title = String(nat.title || '').toLowerCase();
    if (norm.includes('korean') && title.includes('korean')) {
      const southKorea = countryOptions.find((c) => {
        const t = String(c.title || '').toLowerCase();
        return t.includes('korea') && (t.includes('south') || t.includes('republic'));
      });
      if (southKorea?.value) return String(southKorea.value);
    }
  }

  return '';
}

/** Match nationality display text to B04 TOTAL_CODE */
export function resolveNationalityCdFromText(
  nationalityText: string,
  nationalityOptions: CodeOption[],
): string {
  const norm = nationalityText.trim().toLowerCase();
  if (!norm) return '';
  for (const opt of nationalityOptions) {
    const title = String(opt.title || '').toLowerCase();
    if (title === norm) return String(opt.value || '');
    if (norm.includes('korean') && title.includes('korean')) return String(opt.value || '');
  }
  return '';
}
